// @ts-nocheck
import api from './api';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  avatar?: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface UserResponse {
  user: User;
}

// Authentication API endpoints
export const authApi = {
  /**
   * Get Google OAuth authorization URL
   */
  getGoogleAuthUrl: (): Promise<{ authUrl: string }> => {
    return api.get('/auth/google');
  },

  /**
   * Verify Google ID token (for direct frontend auth)
   */
  verifyGoogleToken: (idToken: string): Promise<AuthResponse> => {
    return api.post('/auth/google/verify', { idToken });
  },

  /**
   * Get current authenticated user
   */
  getCurrentUser: (): Promise<UserResponse> => {
    return api.get('/auth/me');
  },

  /**
   * Logout current user
   */
  logout: (): Promise<{ success: boolean; message: string }> => {
    return api.post('/auth/logout');
  },

  /**
   * Refresh JWT token (placeholder for future implementation)
   */
  refreshToken: (refreshToken: string): Promise<AuthResponse> => {
    return api.post('/auth/refresh', { refreshToken });
  },
};

/**
 * Google OAuth helper functions for frontend
 */
export const googleAuth = {
  /**
   * Initiate Google OAuth flow by redirecting to backend
   */
  initiateLogin: async () => {
    try {
      const { authUrl } = await authApi.getGoogleAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to initiate Google login:', error);
      throw new Error('Failed to start authentication process');
    }
  },

  /**
   * Handle callback from Google OAuth (extract token from URL)
   */
  handleCallback: (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const error = urlParams.get('error');

    if (error) {
      console.error('OAuth error:', error);
      throw new Error(error);
    }

    return token;
  },

  /**
   * Clean up callback URL parameters
   */
  cleanupCallback: () => {
    // Remove token and error params from URL without page reload
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    url.searchParams.delete('error');
    window.history.replaceState({}, '', url.toString());
  },
};

/**
 * Token management utilities
 */
export const tokenUtils = {
  /**
   * Get JWT token from localStorage
   */
  getToken: (): string | null => {
    return localStorage.getItem('auth_token');
  },

  /**
   * Set JWT token in localStorage
   */
  setToken: (token: string): void => {
    localStorage.setItem('auth_token', token);
  },

  /**
   * Remove JWT token from localStorage
   */
  removeToken: (): void => {
    localStorage.removeItem('auth_token');
  },

  /**
   * Check if user is authenticated (has valid token)
   */
  isAuthenticated: (): boolean => {
    const token = tokenUtils.getToken();
    if (!token) return false;

    try {
      // Basic JWT structure check
      const parts = token.split('.');
      if (parts.length !== 3) return false;

      // Decode payload to check expiration
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      return payload.exp > currentTime;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  },

  /**
   * Decode JWT token payload
   */
  decodeToken: (token?: string): any | null => {
    const tokenToUse = token || tokenUtils.getToken();
    if (!tokenToUse) return null;

    try {
      const parts = tokenToUse.split('.');
      if (parts.length !== 3) return null;

      return JSON.parse(atob(parts[1]));
    } catch (error) {
      console.error('Token decode error:', error);
      return null;
    }
  },
};